// covered-by: frontends/carrie/src/ask-v2/conversation/__tests__/AskV2ConversationShell.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ChatPage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/home/__tests__/AskV2Home.test.tsx
import { type ReactNode, useRef } from "react";

import { useReadingMessage } from "./useReadingMessage";

import { useAutoScroll } from "../../utils/useAutoScroll";
import { TextSelectionToolbar } from "../transport/TextSelectionToolbar";

type AskV2ConversationShellProps = {
  testId: string;
  header?: ReactNode;
  tabs?: ReactNode;
  /** Omitted when `fullBleed` — composer lives inside the review workspace left column. */
  composer?: ReactNode;
  /** Review workspace fills the content column edge-to-edge (no 720px cap). */
  fullBleed?: boolean;
  rail?: ReactNode;
  children: ReactNode;
  /**
   * Right half of the chat screen. Rendered beside the chat column and
   * inside the area the narrow rail does not occupy.
   */
  split?: ReactNode;
  /** Omitted when `fullBleed` — review feed mounts its own TextSelectionToolbar. */
  onAddQuotedContext?: (input: {
    text: string;
    sourceMessageId?: string;
  }) => void;
  /** When set with scrollDeps, pins the thread to the latest messages on load. */
  scrollResetKey?: unknown;
  scrollDeps?: readonly unknown[];
  autoScrollEnabled?: boolean;
  /**
   * See `UseAutoScrollOptions.sentSignal` — a local send outranks the hold.
   * Every consumer that renders a send-capable `composer` must pass this
   * (`transport.sending`). Omitting it leaves the sent row below the fold for
   * any reader who had scrolled up: the sticky-up guard is still latched when
   * the optimistic row renders and nothing else in ask-v2 clears it on send.
   * (The `autoScrollEnabled` hold compounds it once `inFlight` catches up, but
   * it is not the blocker on the commit that first paints the row.)
   * Pass it unconditionally: the shell holds it while auto-scroll is off, so a
   * send can never pin a scroller parked on non-thread content. All three
   * consumers do: `AskV2ChatPage`, `AskV2ProjectPage`, and `AskV2Home`'s
   * pre-navigate thread — Home matters most, because that shell mounts inside
   * the pre-ACK window with `sending` already true, so the mount itself is the
   * rising edge that arms the pin. A new consumer that wires `scrollDeps` +
   * `autoScrollEnabled` off `transport.inFlight` and skips this prop is the
   * one bug this doc exists to prevent.
   */
  scrollSentSignal?: boolean;
  /** The answer the reader scrolls to, or null at the bottom (`useReadingMessage`). */
  onReadingMessageChange?: (messageId: string | null) => void;
};

export function AskV2ConversationShell({
  testId,
  header,
  tabs,
  composer,
  fullBleed = false,
  rail,
  split,
  children,
  onAddQuotedContext,
  scrollResetKey,
  scrollDeps,
  autoScrollEnabled = true,
  scrollSentSignal,
  onReadingMessageChange,
}: AskV2ConversationShellProps) {
  const autoScrollActive = scrollDeps != null;
  // Dropping `scrollDeps` parks non-thread content in this SAME scroll
  // container: `AskV2ProjectPage`'s private-chats tab leaves `fullBleed` false
  // and only swaps `children`, so the div below is reused as-is. While
  // auto-scroll is off nothing tracks the sticky-up state — `handleScroll` is
  // unwired — and `scrollResetKey` (the shared chat id) does not change on the
  // way back, so a reader who had scrolled up before leaving the feed returned
  // to a thread `useAutoScroll` refused to pin, left at whatever offset the
  // other tab's content had scrolled the shared container to. Coming back is a
  // fresh load of the thread. The bump is a `resetSignal` the hook lists as a
  // layout-effect dependency, so the pin lands in THIS commit even while a
  // turn is still in flight (`enabled` false would otherwise re-latch from
  // the parked offset). Edge-triggered, so a reader scrolled up in a
  // continuously-active thread is still left alone.
  // why: the count has to change before `useAutoScroll` runs. Arming a ref
  // after the hook returns never schedules the effect when `enabled` stays
  // false.
  const reactivationRef = useRef(0);
  const prevAutoScrollActiveRef = useRef(autoScrollActive);
  if (prevAutoScrollActiveRef.current !== autoScrollActive) {
    prevAutoScrollActiveRef.current = autoScrollActive;
    if (autoScrollActive) reactivationRef.current += 1;
  }
  // why-not: do not read the `fullBleed` swap as a remount, and do not reach
  // for a second mechanism to cover it. It swaps the scroller for a plain
  // `overflow-hidden` div in the same DOM slot, and React reconciles the two
  // branches in place (same type, same position, no key), so the node is
  // reused: the reader's scroll position is clamped away while the review is
  // open, and `useAutoScroll`'s sticky-up latch — a ref the swap never
  // touches — survives it. Nothing in the swap clears that latch. The return
  // leg is pinned by the `resetSignal` bump above, because every caller that
  // passes `fullBleed` drops `scrollDeps` with it (`AskV2ChatPage`,
  // `AskV2ProjectPage`), so closing the review IS an auto-scroll reactivation
  // edge. Delete that bump and the reopened thread stays parked where the
  // reader left it.
  //
  // why: hold the last thread identity while auto-scroll is off, because
  // `useAutoScroll` force-pins on a reset edge and must not take one for a
  // container that is not the thread. `AskV2ProjectPage` parks its
  // private-chats tab in this same scroller with `scrollDeps` dropped while
  // still passing `scrollResetKey={sharedChatId}` — an id that arrives late
  // (the shared feed chat is created lazily) or changes on a project switch —
  // and a reader part-way down the chats list must not be dropped to the
  // bottom by it.
  const heldResetKeyRef = useRef(scrollResetKey);
  if (autoScrollActive) heldResetKeyRef.current = scrollResetKey;
  // why: same reason `heldResetKeyRef` is held and `onScroll` is unwired — a
  // send is only this container's business while this container IS the thread.
  // Dropping `scrollDeps` is how a caller says it is not, and both consumers
  // keep passing `scrollSentSignal={transport.sending}` across that line: the
  // review workspace hosts the very SummonBar that writes `sending` (its
  // `composer` prop is the page's feed composer) while `fullBleed` has
  // detached this hook's scroller, and the rail's review-open control is
  // reachable from `AskV2ProjectPage`'s private-chats tab, which parks that
  // list in this same scroller. Ungated, the rising edge arms the hook's
  // force-pin against a null element, the hook deliberately stays armed, and
  // the next commit that re-attaches a scroller — the chats list the reader
  // left part-way down — spends the one-shot on it. Nothing is lost by
  // gating: the review feed has its OWN `useAutoScroll` with its own
  // `sentSignal` (AskV2ReviewWorkspace), and the return leg to a real thread
  // is already pinned by the reactivation bump above.
  const sentSignalForThread = autoScrollActive ? scrollSentSignal : false;
  const { scrollContainerRef, contentRef, handleScroll } = useAutoScroll(
    scrollDeps ? [...scrollDeps] : [],
    heldResetKeyRef.current,
    {
      enabled: autoScrollActive && autoScrollEnabled,
      sentSignal: sentSignalForThread,
      resetSignal: reactivationRef.current,
    },
  );
  useReadingMessage(scrollContainerRef, onReadingMessageChange);

  return (
    <div
      data-testid={testId}
      className="ask-puntt-surface relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
    >
      {header}
      {tabs}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* why: `contents` while closed keeps the chat column a flex item beside the rail. A real box only while the split is open. The project page omits the rail then, so this box is the whole row and the two w-1/2 children are the screen. */}
        <div
          className={
            split != null
              ? "flex min-h-0 min-w-0 flex-1 overflow-hidden"
              : "contents"
          }
        >
          <div
            data-testid="ask-v2-conversation-column"
            className={
              split != null
                ? "flex h-full min-h-0 w-1/2 min-w-0 flex-col overflow-hidden"
                : "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
            }
          >
            {fullBleed ? (
              <div
                className="relative flex min-h-0 flex-1 overflow-hidden"
                data-testid="ask-v2-messages"
              >
                {children}
              </div>
            ) : (
              <>
                <div
                  ref={scrollContainerRef}
                  onScroll={autoScrollActive ? handleScroll : undefined}
                  className="relative flex-1 overflow-y-auto px-4 py-6"
                  data-testid="ask-v2-messages"
                >
                  {onAddQuotedContext ? (
                    <TextSelectionToolbar
                      containerRef={scrollContainerRef}
                      onAddContext={onAddQuotedContext}
                    />
                  ) : null}
                  <div
                    ref={contentRef}
                    className="mx-auto w-full max-w-[720px] space-y-4"
                  >
                    {children}
                  </div>
                </div>
                {composer ? (
                  <div
                    className="shrink-0 p-4"
                    data-testid="ask-v2-composer-region"
                  >
                    <div className="mx-auto w-full max-w-[720px]">
                      {composer}
                    </div>
                  </div>
                ) : null}
              </>
            )}
          </div>
          {split}
        </div>
        {rail}
      </div>
    </div>
  );
}
